import React from "react";
import {
    InvestGrid,
    InvestCard,
    CardTop,
    CardIcon,
    CardTitle,
    StatusPill,
    CardBody,
    CardActions,
    SmallBtn,
    InvestTable,
    TableHeader,
    TableRow,
    CompletionBar,
    CompletionLabel,
    BarOuter,
    BarInner,
    Toolbar,
    FilterTabs,
    FilterTab,
    QuickOrderBtn,
    ViewToggle,
    ViewToggleBtn,
    EmptyState,
    LoadingWrapper,
} from "./ANCInvestigationStyles";

/* ─── Helper maps ─── */
const CATEGORY_ICONS = {
    serology: "🧬",
    haematology: "🩸",
    biochemistry: "⚗",
    urinalysis: "🧪",
    parasitology: "🔬",
    radiology: "📡",
};

const STATUS_LABELS = {
    resulted: "Resulted",
    pending: "Pending",
    not_ordered: "Not Ordered",
};

const STATUS_ICONS = {
    resulted: "✓",
    pending: "⏱",
    not_ordered: "○",
};

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch (e) { return dateStr; }
}

function formatDateShort(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    } catch (e) { return dateStr; }
}

/**
 * ANCInvestigationPanel — Visual panel showing all ANC tests with status and results
 */
const ANCInvestigationPanel = function(props) {
    const filteredPanel = props.filteredPanel || [];
    const summary = props.summary || {};
    const loading = props.loading;
    const gestationalAge = props.gestationalAge;
    const onOrder = props.onOrder;
    const onRecordResult = props.onRecordResult;
    const onQuickOrder = props.onQuickOrder;
    const saving = props.saving;
    const activeView = props.activeView || "cards";
    const setActiveView = props.setActiveView;
    const filterCategory = props.filterCategory || "all";
    const setFilterCategory = props.setFilterCategory;

    if (loading) {
        return (
            <LoadingWrapper>
                <span>⏳</span> Loading investigations...
            </LoadingWrapper>
        );
    }

    return (
        <div>
            {/* ─── Completion bar ─── */}
            <CompletionBar>
                <CompletionLabel>
                    <span>
                        Screening Completion — {summary.resulted || 0} of {summary.total || 0} standard tests resulted
                        {gestationalAge ? " · GA: " + gestationalAge + " weeks" : ""}
                    </span>
                    <span className="percent">{summary.completionPercent || 0}%</span>
                </CompletionLabel>
                <BarOuter>
                    <BarInner percent={summary.completionPercent || 0} />
                </BarOuter>
                {summary.abnormal > 0 && (
                    <div style={{ marginTop: "8px", fontSize: "0.78rem", color: "#b71c1c", fontWeight: 500 }}>
                        ⚠ {summary.abnormal} abnormal result{summary.abnormal > 1 ? "s" : ""} flagged
                    </div>
                )}
                {summary.dueNow > 0 && (
                    <div style={{ marginTop: "4px", fontSize: "0.78rem", color: "#e65100", fontWeight: 500 }}>
                        🔔 {summary.dueNow} test{summary.dueNow > 1 ? "s" : ""} due at current gestational age
                    </div>
                )}
            </CompletionBar>

            {/* ─── Toolbar ─── */}
            <Toolbar>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                    <ViewToggle>
                        <ViewToggleBtn active={activeView === "cards"} onClick={function() { setActiveView("cards"); }}>
                            ▦ Cards
                        </ViewToggleBtn>
                        <ViewToggleBtn active={activeView === "table"} onClick={function() { setActiveView("table"); }}>
                            ▤ Table
                        </ViewToggleBtn>
                    </ViewToggle>
                    <FilterTabs>
                        <FilterTab active={filterCategory === "all"} onClick={function() { setFilterCategory("all"); }}>All</FilterTab>
                        <FilterTab active={filterCategory === "due"} onClick={function() { setFilterCategory("due"); }}>
                            Due Now {summary.dueNow > 0 ? "(" + summary.dueNow + ")" : ""}
                        </FilterTab>
                        <FilterTab active={filterCategory === "abnormal"} onClick={function() { setFilterCategory("abnormal"); }}>
                            Abnormal {summary.abnormal > 0 ? "(" + summary.abnormal + ")" : ""}
                        </FilterTab>
                        <FilterTab active={filterCategory === "pending"} onClick={function() { setFilterCategory("pending"); }}>Pending</FilterTab>
                        <FilterTab active={filterCategory === "serology"} onClick={function() { setFilterCategory("serology"); }}>Serology</FilterTab>
                        <FilterTab active={filterCategory === "haematology"} onClick={function() { setFilterCategory("haematology"); }}>Haematology</FilterTab>
                    </FilterTabs>
                </div>
                {onQuickOrder && (
                    <QuickOrderBtn onClick={onQuickOrder} disabled={saving}>
                        ⚡ {saving ? "Ordering..." : "Quick Order — First Visit Panel"}
                    </QuickOrderBtn>
                )}
            </Toolbar>

            {/* ─── Cards View ─── */}
            {activeView === "cards" && (
                <InvestGrid>
                    {filteredPanel.length === 0 && (
                        <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "40px 0", color: "#90a4ae" }}>
                            No investigations match the current filter
                        </div>
                    )}
                    {filteredPanel.map(function(item) {
                        const status = item.status;
                        const icon = CATEGORY_ICONS[item.category] || "🧪";

                        return (
                            <InvestCard
                                key={item.key}
                                status={status}
                                abnormal={item.isAbnormal}
                                isDue={item.isDueNow && status === "not_ordered"}
                            >
                                <CardTop>
                                    <CardIcon status={status} abnormal={item.isAbnormal}>{icon}</CardIcon>
                                    <CardTitle>
                                        <div className="card-name">{item.label}</div>
                                        <div className="card-specimen">{item.specimen}{item.conditional ? " · Conditional" : ""}</div>
                                    </CardTitle>
                                    <StatusPill status={status}>
                                        {STATUS_ICONS[status]} {STATUS_LABELS[status]}
                                    </StatusPill>
                                </CardTop>

                                <CardBody abnormal={item.isAbnormal}>
                                    {status === "resulted" && (
                                        <div>
                                            <div className="result-row">
                                                <span className="result-label">Result:</span>
                                                <span className="result-value">{item.resultValue}</span>
                                                {item.isAbnormal && (
                                                    <span className="abnormal-flag">⚠ ABNORMAL</span>
                                                )}
                                            </div>
                                            <div className="result-date">
                                                Result date: {formatDate(item.resultDate)}
                                            </div>
                                        </div>
                                    )}
                                    {status === "pending" && item.latest && (
                                        <div>
                                            <div className="result-row">
                                                <span className="result-label">Ordered:</span>
                                                <span style={{ fontSize: "0.84rem", color: "#546e7a" }}>
                                                    {formatDate(item.latest.ordered_date)}
                                                </span>
                                            </div>
                                            <div className="result-date">Awaiting results...</div>
                                        </div>
                                    )}
                                    {status === "not_ordered" && (
                                        <div className="recommended-text">
                                            {item.recommendedWeeks.length > 0
                                                ? "Recommended at: " + item.recommendedWeeks.join(", ") + " weeks"
                                                : "Order when clinically indicated"}
                                        </div>
                                    )}
                                    {item.totalOrdered > 1 && (
                                        <div className="recommended-text" style={{ marginTop: "4px" }}>
                                            📊 {item.totalOrdered} total orders for this test
                                        </div>
                                    )}
                                </CardBody>

                                <CardActions>
                                    {status === "not_ordered" && onOrder && (
                                        <SmallBtn color="#00695c" onClick={function() { onOrder(item.key); }}>
                                            Order Test
                                        </SmallBtn>
                                    )}
                                    {status === "pending" && onRecordResult && (
                                        <SmallBtn color="#ef6c00" onClick={function() { onRecordResult(item.latest || item); }}>
                                            Enter Result
                                        </SmallBtn>
                                    )}
                                    {status === "resulted" && onOrder && (
                                        <SmallBtn color="#546e7a" onClick={function() { onOrder(item.key); }}>
                                            Re-Order
                                        </SmallBtn>
                                    )}
                                    {status === "resulted" && onRecordResult && (
                                        <SmallBtn color="#00695c" onClick={function() { onRecordResult(item.latest || item); }}>
                                            View / Update
                                        </SmallBtn>
                                    )}
                                </CardActions>
                            </InvestCard>
                        );
                    })}
                </InvestGrid>
            )}

            {/* ─── Table View ─── */}
            {activeView === "table" && (
                <InvestTable>
                    <TableHeader>
                        <div>Investigation</div>
                        <div>Status</div>
                        <div>Ordered</div>
                        <div>Result</div>
                        <div>Date</div>
                        <div>Actions</div>
                    </TableHeader>
                    {filteredPanel.length === 0 && (
                        <div style={{ textAlign: "center", padding: "30px 0", color: "#90a4ae", fontSize: "0.85rem" }}>
                            No investigations match the current filter
                        </div>
                    )}
                    {filteredPanel.map(function(item) {
                        const status = item.status;
                        return (
                            <TableRow key={item.key} abnormal={item.isAbnormal}>
                                <div className="row-test">
                                    {CATEGORY_ICONS[item.category] || "🧪"} {item.label}
                                    {item.isDueNow && status === "not_ordered" && (
                                        <span style={{ marginLeft: "6px", fontSize: "0.65rem", background: "#ff6f00", color: "#fff", padding: "1px 5px", borderRadius: "3px", fontWeight: 600 }}>DUE</span>
                                    )}
                                </div>
                                <div>
                                    <StatusPill status={status}>
                                        {STATUS_ICONS[status]} {STATUS_LABELS[status]}
                                    </StatusPill>
                                </div>
                                <div className="row-date">
                                    {item.latest && item.latest.ordered_date ? formatDateShort(item.latest.ordered_date) : "—"}
                                </div>
                                <div className="row-result">
                                    {item.resultValue || "—"}
                                    {item.isAbnormal && (
                                        <span style={{ marginLeft: "6px", fontSize: "0.65rem", background: "#ffcdd2", color: "#b71c1c", padding: "1px 5px", borderRadius: "3px", fontWeight: 600 }}>⚠</span>
                                    )}
                                </div>
                                <div className="row-date">{item.resultDate ? formatDateShort(item.resultDate) : "—"}</div>
                                <div>
                                    {status === "not_ordered" && onOrder && (
                                        <SmallBtn color="#00695c" onClick={function() { onOrder(item.key); }}>Order</SmallBtn>
                                    )}
                                    {status === "pending" && onRecordResult && (
                                        <SmallBtn color="#ef6c00" onClick={function() { onRecordResult(item.latest || item); }}>Result</SmallBtn>
                                    )}
                                    {status === "resulted" && onRecordResult && (
                                        <SmallBtn color="#546e7a" onClick={function() { onRecordResult(item.latest || item); }}>View</SmallBtn>
                                    )}
                                </div>
                            </TableRow>
                        );
                    })}
                </InvestTable>
            )}
        </div>
    );
};

export default ANCInvestigationPanel;
